import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ROWS = [
  { nameKey: "markets.oneaccount.m1.name", dataKey: "forex",       bg: false },
  { nameKey: "markets.oneaccount.m2.name", dataKey: "indices",     bg: true  },
  { nameKey: "markets.oneaccount.m3.name", dataKey: "commodities", bg: false },
  { nameKey: "markets.oneaccount.m4.name", dataKey: "stocks",      bg: true  },
  { nameKey: "markets.oneaccount.m5.name", dataKey: "crypto",      bg: false },
];

const COLS = [
  {
    labelKey: "markets.compare.col.instruments",
    render: (t, dataKey) => (
      <span className="font-['Montserrat'] font-extrabold text-[14px] text-[#020202]">
        {t(`markets.compare.${dataKey}.instruments`)}
      </span>
    ),
  },
  {
    labelKey: "markets.compare.col.coverage",
    render: (t, dataKey) => (
      <span className="font-['Inter'] font-normal text-[14px] text-[#888]">
        {t(`markets.compare.${dataKey}.coverage`)}
      </span>
    ),
  },
  {
    labelKey: "markets.compare.col.advantage",
    render: (t, dataKey) => (
      <span className="bg-[#fcefea] text-[#ee5414] font-['Inter'] font-medium text-[12px] px-[14px] py-[5px] rounded-full whitespace-nowrap">
        {t(`markets.compare.${dataKey}.advantage`)}
      </span>
    ),
  },
  {
    labelKey: "markets.compare.col.leverage",
    render: (t, dataKey) => (
      <span className="font-['Montserrat'] font-extrabold text-[14px] whitespace-nowrap">
        <span className="text-[#020202]">Up to </span>
        <span className="text-[#f04e0a]">{t(`markets.compare.${dataKey}.leverage`)}</span>
      </span>
    ),
  },
];

function CompareTable() {
  const { t } = useTranslation();
  const [colIndex, setColIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const prev = () => {
    if (colIndex === 0) return;
    setDirection(-1);
    setColIndex((i) => i - 1);
  };

  const next = () => {
    if (colIndex === COLS.length - 1) return;
    setDirection(1);
    setColIndex((i) => i + 1);
  };

  const activeCol = COLS[colIndex];

  return (
    <section className="mt">
      <div className="glcontainer flex flex-col gap-[40px] xl:gap-[60px]">
        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-[20px]">
          <h2 className="font-['Montserrat'] font-black text-[24px] xl:text-[46px] text-[#0a0a0a] tracking-[-0.92px] uppercase leading-none xl:max-w-[504px]">
            {t("markets.compare.title")}
          </h2>
          <p className="font-['Inter'] font-normal text-[14px] xl:text-[18px] text-[#717171] leading-[1.4] xl:max-w-[600px]">
            {t("markets.compare.desc")}
          </p>
        </div>

        {/* Mobile table with swipeable columns */}
        <div className="xl:hidden flex flex-col pt-[16px]">
          {/* Column header row with arrows */}
          <div className="flex items-center border-b-2 border-[#0a0a0a] pb-[19px]">
            <div className="shrink-0 w-[161px]">
              <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase">
                {t("markets.compare.col.market")}
              </span>
            </div>
            <div className="flex-1 flex items-center justify-between">
              <button
                onClick={prev}
                disabled={colIndex === 0}
                className="flex items-center justify-center w-[20px] h-[20px] disabled:opacity-30"
                aria-label="Previous column"
              >
                <ChevronLeft size={14} className="text-[#020202]" />
              </button>
              <div
                key={colIndex}
                className={`swiper-card-enter ${direction > 0 ? "swiper-card-next" : "swiper-card-prev"}`}
              >
                <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase">
                  {t(activeCol.labelKey)}
                </span>
              </div>
              <button
                onClick={next}
                disabled={colIndex === COLS.length - 1}
                className="flex items-center justify-center w-[20px] h-[20px] disabled:opacity-30"
                aria-label="Next column"
              >
                <ChevronRight size={14} className="text-[#020202]" />
              </button>
            </div>
          </div>

          {/* Body rows */}
          {ROWS.map((row, i) => {
            const isLast = i === ROWS.length - 1;
            return (
              <div
                key={row.dataKey}
                className={`flex items-center h-[50px] ${row.bg ? "bg-[#f7f7f7]" : !isLast ? "border-b border-[#ebebeb]" : ""}`}
              >
                <div className="shrink-0 w-[161px]">
                  <span className="font-['Montserrat'] font-extrabold text-[14px] text-[#020202] tracking-[-0.17px] uppercase">
                    {t(row.nameKey)}
                  </span>
                </div>
                <div
                  key={`${row.dataKey}-${colIndex}`}
                  className={`flex-1 swiper-card-enter ${direction > 0 ? "swiper-card-next" : "swiper-card-prev"}`}
                >
                  {activeCol.render(t, row.dataKey)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="hidden xl:flex xl:flex-col pt-[16px]">
          {/* Header row */}
          <div className="flex items-center border-b-2 border-[#0a0a0a] pb-[19px]">
            <div className="shrink-0 w-[242px] pl-[20px]">
              <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase">
                {t("markets.compare.col.market")}
              </span>
            </div>
            <div className="w-[204px] pl-[32px]">
              <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase">
                {t("markets.compare.col.instruments")}
              </span>
            </div>
            <div className="w-[258px] px-[16px]">
              <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase">
                {t("markets.compare.col.coverage")}
              </span>
            </div>
            <div className="w-[258px] px-[16px]">
              <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase">
                {t("markets.compare.col.advantage")}
              </span>
            </div>
            <div className="w-[211px] flex justify-center">
              <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase">
                {t("markets.compare.col.leverage")}
              </span>
            </div>
          </div>

          {/* Body rows */}
          {ROWS.map((row, i) => {
            const isLast = i === ROWS.length - 1;
            return (
              <div
                key={row.dataKey}
                className={`flex items-center py-[34px] ${row.bg ? "bg-[#f7f7f7]" : !isLast ? "border-b border-[#ebebeb]" : ""}`}
              >
                <div className="shrink-0 w-[242px] pl-[20px]">
                  <span className="font-['Montserrat'] font-extrabold text-[17px] text-[#020202] tracking-[-0.17px] uppercase">
                    {t(row.nameKey)}
                  </span>
                </div>
                <div className="w-[204px] pl-[32px]">
                  <span className="font-['Montserrat'] font-extrabold text-[17px] text-[#020202]">
                    {t(`markets.compare.${row.dataKey}.instruments`)}
                  </span>
                </div>
                <div className="w-[258px] px-[16px]">
                  <span className="font-['Inter'] font-normal text-[14px] text-[#888]">
                    {t(`markets.compare.${row.dataKey}.coverage`)}
                  </span>
                </div>
                <div className="w-[258px] px-[16px]">
                  <span className="bg-[#fcefea] text-[#ee5414] font-['Inter'] font-medium text-[12px] px-[14px] py-[5px] rounded-full whitespace-nowrap">
                    {t(`markets.compare.${row.dataKey}.advantage`)}
                  </span>
                </div>
                <div className="w-[211px] flex justify-center">
                  <span className="font-['Montserrat'] font-extrabold text-[16px] whitespace-nowrap">
                    <span className="text-[#020202]">Up to </span>
                    <span className="text-[#f04e0a]">{t(`markets.compare.${row.dataKey}.leverage`)}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default CompareTable;
